<script>
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iMenu,
  iMenuTriggerAlignOptions,
  iMenuItem,
  iSwitch,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import { downloadDocument } from '@/utils/download';
import * as signalNames from '@/constants/signalNames';
import palette from '@inscribe/signal/dist/palette';
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  components: { iButton, iMenu, iMenuItem, iSwitch, iTooltip },
  props: {
    signalGroup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      iMenuTriggerAlignOptions,
      iTooltipAlignmentMode,
      learnMoreBaseLink: 'https://help.inscribe.ai/en/articles/',
      palette,
      signalNames,
      switchEnabled: false,
      signalToolsUUID: crypto.randomUUID(),
    };
  },
  mounted() {
    this.switchEnabled = this.isSignalGroupEnabled();
    this.setGroupsChangeCallback(() => {
      this.switchEnabled = this.isSignalGroupEnabled();
    });
  },
  computed: {
    ...mapGetters([
      'getDisabledGroups',
      'isChatAgentEnabled',
      'currentConversationUuid',
    ]),
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'openChat']),
    ...mapMutations(['setDisabledGroup', 'setGroupsChangeCallback']),
    openChatAgent(signalGroup) {
      this.openChat();
      this.submitConversationMessage({
        conversationUuid: this.currentConversationUuid,
        userMessageContent: `What does this ${signalGroup.title} signal mean in this context?`,
        contextCustomerUuids: [this.$route.params.customer_id],
        contextDocumentIds: [this.$route.params.document_id],
      });
    },
    isSignalGroupEnabled() {
      return !this.getDisabledGroups.includes(this.signalGroup.signalKey);
    },
    downloadXray(signalGroup) {
      const signal = signalGroup.signals?.length
        ? signalGroup.signals[0]
        : signalGroup.details;
      if (signal.recovered_version) {
        downloadDocument(signal.recovered_version.url, 'recovered-version.pdf');
      }
    },
    openDecisionEngine() {
      this.$router.push({ name: 'decision-engine-detectors' });
    },
    onSignalGroupSwitch(value, signalGroup) {
      this.setDisabledGroup({ group: signalGroup, value });
    },
    stopPropagation(evt) {
      evt.stopPropagation();
    },
  },
};
</script>

<template>
  <div
    class="signal-tools d-flex flex-row align-items-center i-gap-3"
    @click="stopPropagation"
  >
    <i-switch
      v-if="signalGroup.hasRegions"
      v-model="switchEnabled"
      size="xs"
      @input="(e) => onSignalGroupSwitch(e, signalGroup)"
    />
    <template v-if="isChatAgentEnabled">
      <i-button
        left-icon="medium-chat-square-text"
        :id="`ask-assistant-button-${signalToolsUUID}`"
        :type="iButtonTypes.NEUTRAL"
        :size="iButtonSizes.MEDIUM"
        :button-style="iButtonStyles.GHOST"
        @button-click="openChatAgent(signalGroup)"
      ></i-button>
      <i-tooltip
        :target="`#ask-assistant-button-${signalToolsUUID}`"
        title="Ask Assistant"
        :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
      />
    </template>

    <i-menu
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="250"
      @i-menu-open-changed="$emit('i-menu-open-changed', $event)"
    >
      <template #trigger>
        <i-button
          class="trigger-button"
          data-testid="signal-actions-button"
          size="md"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          :left-icon="'medium-more-menu-vertical'"
        />
      </template>

      <template #items>
        <!-- Download  -->
        <i-menu-item
          v-if="signalGroup.signalKey === signalNames.XRAY"
          label="Download"
          icon="small_download_button_2"
          @item-click="downloadXray(signalGroup)"
        ></i-menu-item>

        <!-- manage Signals -->
        <i-menu-item
          label="Manage Detectors"
          icon="small-setting-cog"
          @item-click="openDecisionEngine()"
        ></i-menu-item>

        <!-- Learn More  -->
        <i-menu-item
          label="Learn More"
          icon="small-info-circle"
          right-icon="medium_link_square_1"
          :right-icon-color="palette['light-neutral-70']"
          :link-external-url="
            learnMoreBaseLink + `${signalGroup.helpArticle || ''}`
          "
        ></i-menu-item>
      </template>
    </i-menu>
  </div>
</template>
